import { useState } from "react";
import axios from "axios";
import { Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useCart } from "../context/CartContext";
import { formatCOP } from "../data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEPARTAMENTOS = [
  "Antioquia",
  "Atlántico",
  "Bogotá D.C.",
  "Bolívar",
  "Boyacá",
  "Caldas",
  "Cundinamarca",
  "Magdalena",
  "Nariño",
  "Norte de Santander",
  "Quindío",
  "Risaralda",
  "Santander",
  "Tolima",
  "Valle del Cauca",
  "Otro",
];

const initialForm = {
  fullName: "",
  email: "",
  phone: "",
  address: "",
  city: "Cúcuta",
  department: "Norte de Santander",
  notes: "",
};

const PAYMENT_METHODS = [
  {
    id: "bold_mock",
    label: "Tarjeta / PSE (Bold)",
    desc: "Pago seguro con tarjeta o débito en línea.",
  },
  {
    id: "sistecredito_mock",
    label: "Sistecrédito",
    desc: "Compra ahora, paga a cuotas. Sin tarjeta de crédito.",
  },
];

export const CheckoutDialog = ({ open, onOpenChange, onSuccess }) => {
  const { items, subtotal, clearCart } = useCart();
  const [form, setForm] = useState(initialForm);
  const [paymentMethod, setPaymentMethod] = useState("bold_mock");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // order object

  const SHIPPING = subtotal >= 200000 || subtotal === 0 ? 0 : 15000;
  const total = subtotal + SHIPPING;

  const updateField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const validate = () => {
    if (!form.fullName.trim()) return "Nombre completo es requerido";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Email inválido";
    if (form.phone.replace(/\D/g, "").length < 7) return "Teléfono inválido";
    if (!form.address.trim()) return "Dirección es requerida";
    if (!form.city.trim()) return "Ciudad es requerida";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    if (items.length === 0) {
      toast.error("Tu carrito está vacío");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        customer: form,
        items: items.map((it) => ({
          productId: it.productId,
          title: it.title,
          sizeId: it.sizeId,
          sizeLabel: it.sizeLabel,
          frameId: it.frameId,
          frameLabel: it.frameLabel,
          unitPrice: it.unitPrice,
          qty: it.qty,
        })),
        subtotal,
        shipping: SHIPPING,
        total,
        payment_method: paymentMethod,
      };
      const { data } = await axios.post(`${API}/orders`, payload);
      if (data.bold_payment_link_url) {
        // Bold real activo: redirigir a la pasarela hosted.
        clearCart();
        window.location.href = data.bold_payment_link_url;
        return;
      }
      setDone(data);
      clearCart();
    } catch (err) {
      console.error(err);
      toast.error("No pudimos procesar tu orden. Inténtalo de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (o) => {
    if (!o) {
      onOpenChange(false);
      // Reset state after close animation
      setTimeout(() => {
        if (done) {
          setDone(null);
          setForm(initialForm);
          if (onSuccess) onSuccess();
        }
      }, 300);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="checkout-dialog"
        aria-describedby={undefined}
        className="max-w-2xl bg-slate-50 border-slate-200 p-0 overflow-hidden max-h-[92vh] flex flex-col"
      >
        {done ? (
          <div
            data-testid="checkout-success"
            className="flex flex-col items-center text-center px-8 py-12"
          >
            <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mb-5">
              <CheckCircle2 className="h-9 w-9 text-green-600" strokeWidth={1.75} />
            </div>
            <DialogHeader className="space-y-2">
              <DialogTitle className="font-serif text-3xl text-slate-900">
                ¡Pedido recibido!
              </DialogTitle>
              <DialogDescription className="text-base text-slate-600 max-w-md">
                Tu orden{" "}
                <span
                  data-testid="checkout-order-id"
                  className="font-mono font-semibold text-slate-900"
                >
                  #{done.order_number}
                </span>{" "}
                fue registrada por {formatCOP(done.total)}. Te enviamos el
                resumen a <span className="font-medium">{done.customer.email}</span>.
              </DialogDescription>
            </DialogHeader>
            {done.payment_status === "paid_mock" && (
              <p className="mt-6 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 max-w-md">
                <ShieldCheck className="inline h-3.5 w-3.5 mr-1" />
                Pago simulado (pasarela aún sin credenciales). Con Bold activo
                redirigimos a la pasarela real.
              </p>
            )}
            <Button
              onClick={() => handleClose(false)}
              data-testid="checkout-success-close"
              className="mt-8 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8"
            >
              Continuar explorando
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader className="px-8 pt-8 pb-4 border-b border-slate-200">
              <DialogTitle className="font-serif text-2xl text-slate-900">
                Finalizar compra
              </DialogTitle>
              <DialogDescription className="text-slate-500">
                Completa tus datos. Te confirmamos por email.
              </DialogDescription>
            </DialogHeader>

            <form
              onSubmit={handleSubmit}
              className="flex-1 overflow-y-auto px-8 py-6 space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label htmlFor="fullName" className="text-slate-700">
                    Nombre completo
                  </Label>
                  <Input
                    id="fullName"
                    data-testid="checkout-fullname"
                    value={form.fullName}
                    onChange={(e) => updateField("fullName", e.target.value)}
                    placeholder="María Ramírez"
                    className="mt-1.5 bg-white"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-slate-700">
                    Email
                  </Label>
                  <Input
                    id="email"
                    data-testid="checkout-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => updateField("email", e.target.value)}
                    placeholder="maria@correo.co"
                    className="mt-1.5 bg-white"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-slate-700">
                    Teléfono
                  </Label>
                  <Input
                    id="phone"
                    data-testid="checkout-phone"
                    value={form.phone}
                    onChange={(e) => updateField("phone", e.target.value)}
                    placeholder="3001234567"
                    className="mt-1.5 bg-white"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="address" className="text-slate-700">
                    Dirección de envío
                  </Label>
                  <Input
                    id="address"
                    data-testid="checkout-address"
                    value={form.address}
                    onChange={(e) => updateField("address", e.target.value)}
                    placeholder="Calle 80 # 11-30, Apto 502"
                    className="mt-1.5 bg-white"
                  />
                </div>
                <div>
                  <Label htmlFor="city" className="text-slate-700">
                    Ciudad
                  </Label>
                  <Input
                    id="city"
                    data-testid="checkout-city"
                    value={form.city}
                    onChange={(e) => updateField("city", e.target.value)}
                    placeholder="Bogotá"
                    className="mt-1.5 bg-white"
                  />
                </div>
                <div>
                  <Label htmlFor="department" className="text-slate-700">
                    Departamento
                  </Label>
                  <select
                    id="department"
                    data-testid="checkout-department"
                    value={form.department}
                    onChange={(e) => updateField("department", e.target.value)}
                    className="mt-1.5 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E85A3C]/30"
                  >
                    {DEPARTAMENTOS.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Payment method */}
              <div>
                <h4 className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-3">
                  Método de pago
                </h4>
                <div
                  data-testid="checkout-payment-methods"
                  className="grid grid-cols-1 sm:grid-cols-2 gap-2"
                >
                  {PAYMENT_METHODS.map((pm) => {
                    const active = paymentMethod === pm.id;
                    return (
                      <button
                        key={pm.id}
                        type="button"
                        onClick={() => setPaymentMethod(pm.id)}
                        data-testid={`checkout-pm-${pm.id}`}
                        className={`text-left rounded-xl border px-4 py-3 transition-all duration-200 ${
                          active
                            ? "border-[#E85A3C] bg-[#E85A3C]/5 ring-1 ring-[#E85A3C]/30"
                            : "border-slate-200 bg-white hover:border-slate-400"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-slate-900">
                            {pm.label}
                          </span>
                          <span
                            className={`h-4 w-4 rounded-full border-2 ${
                              active
                                ? "border-[#E85A3C] bg-[#E85A3C]"
                                : "border-slate-300"
                            }`}
                          />
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{pm.desc}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Order summary */}
              <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
                <h4 className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-3">
                  Resumen
                </h4>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">
                      Subtotal ({items.reduce((s, i) => s + i.qty, 0)} piezas)
                    </span>
                    <span className="text-slate-900 tabular-nums">
                      {formatCOP(subtotal)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Envío</span>
                    <span className="text-slate-900 tabular-nums">
                      {SHIPPING === 0 ? "Gratis" : formatCOP(SHIPPING)}
                    </span>
                  </div>
                  <div className="pt-2 mt-2 border-t border-slate-100 flex justify-between items-baseline">
                    <span className="text-base font-medium text-slate-900">
                      Total
                    </span>
                    <span
                      data-testid="checkout-total"
                      className="font-serif text-2xl text-slate-900 tabular-nums"
                    >
                      {formatCOP(total)}
                    </span>
                  </div>
                </div>
              </div>
            </form>

            <div className="px-8 py-5 border-t border-slate-200 bg-white">
              <Button
                onClick={handleSubmit}
                disabled={submitting || items.length === 0}
                data-testid="checkout-submit"
                className="w-full h-12 rounded-full bg-[#E85A3C] hover:bg-[#D3492D] text-white text-base font-medium shadow-lg shadow-[#E85A3C]/30"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    Pagar {formatCOP(total)} con{" "}
                    {paymentMethod === "sistecredito_mock"
                      ? "Sistecrédito"
                      : "Bold"}
                  </>
                )}
              </Button>
              <p className="text-[11px] text-slate-400 text-center mt-2">
                <ShieldCheck className="inline h-3 w-3 mr-0.5" />
                Pago seguro procesado por{" "}
                {paymentMethod === "sistecredito_mock"
                  ? "Sistecrédito"
                  : "Bold"}
                .
              </p>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
